import React, { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

import invariant from 'tiny-invariant';

import Announcer from '@atlassian/a11y-announcer';

export type AnnouncementContextType = {
	setAnnouncement: (announcement: string) => void;
};

export type AnnouncementProviderProps = {
	children: ReactNode;
	/**
	 * aria-live attribute value for the announcement region.
	 * @default 'polite'
	 */
	liveMode?: 'polite' | 'off' | 'assertive';
	/**
	 * ID to identify our announcement region in tests and when debugging.
	 */
	testId?: string;
};

export const AnnouncementContext: React.Context<AnnouncementContextType> =
	createContext<AnnouncementContextType>(undefined!);

export function AnnouncementProvider({
	children,
	liveMode,
	testId = 'navigation-announcements',
}: AnnouncementProviderProps): React.JSX.Element {
	const [announcement, setAnnouncement] = useState<string>('');

	const contextValue = useMemo(() => ({ setAnnouncement }), []);

	return (
		<>
			<AnnouncementContext.Provider value={contextValue}>{children}</AnnouncementContext.Provider>
			<Announcer testId={testId} message={announcement} liveMode={liveMode} />
		</>
	);
}

export const useAnnouncements = (): AnnouncementContextType => {
	const announcementContext = useContext(AnnouncementContext);

	invariant(
		announcementContext,
		'useAnnouncements must be used inside an initialized AnnouncementProvider',
	);

	return announcementContext;
};

export const useOptionalAnnouncements = (): AnnouncementContextType | undefined => {
	return useContext(AnnouncementContext);
};
